import * as THREE from 'three';
import { AABB, makeAABB } from './types';
import {
  KeepMaterials,
  setBoxWorldUVs,
  setPlaneWorldUVs,
} from './materials';

export type DoorSide = 'n' | 's' | 'e' | 'w';

export type RoomDoor = {
  side: DoorSide;
  /** 0–1 along the wall from the lower/left end. */
  t: number;
  width: number;
  height?: number;
};

export type RoomInfo = {
  id: string;
  name: string;
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
  floorY: number;
  ceilingY: number;
};

type BuildRoomOpts = {
  id: string;
  name: string;
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
  floorY?: number;
  wallH?: number;
  wallT?: number;
  ceiling?: boolean;
  doors?: RoomDoor[];
  floorMat?: THREE.Material;
  wallMat?: THREE.Material;
  ceilMat?: THREE.Material;
  /** Courtyard = open sky; interiors get a soft fill light. */
  lit?: boolean;
};

/**
 * Build one naturally enclosed room: floor, four walls with door gaps, optional ceiling.
 * Walls are solid stone (visual + AABB), so exits only exist through doorways.
 */
export function buildEnclosedRoom(
  parent: THREE.Object3D,
  mats: KeepMaterials,
  solids: AABB[],
  opts: BuildRoomOpts,
): RoomInfo {
  const floorY = opts.floorY ?? 0;
  const wallH = opts.wallH ?? 3.5;
  const wallT = opts.wallT ?? 0.48;
  const ceiling = opts.ceiling !== false;
  const doors = opts.doors ?? [];
  const floorMat = opts.floorMat ?? mats.paving;
  const wallMat = opts.wallMat ?? mats.wall;
  const ceilMat = opts.ceilMat ?? mats.wallDark;
  const doorH = 2.25;

  const { minX, maxX, minZ, maxZ } = opts;
  const cx = (minX + maxX) * 0.5;
  const cz = (minZ + maxZ) * 0.5;
  const sx = maxX - minX;
  const sz = maxZ - minZ;

  const group = new THREE.Group();
  group.name = `Room_${opts.id}`;
  parent.add(group);

  // Floor
  const floorGeo = new THREE.PlaneGeometry(sx + wallT, sz + wallT);
  setPlaneWorldUVs(floorGeo, sx + wallT, sz + wallT, 2.4);
  const floor = new THREE.Mesh(floorGeo, floorMat);
  floor.rotation.x = -Math.PI / 2;
  floor.position.set(cx, floorY + 0.01, cz);
  floor.receiveShadow = true;
  group.add(floor);
  solids.push(makeAABB(cx, floorY - 0.25, cz, sx * 0.5 + wallT, 0.28, sz * 0.5 + wallT));

  const midY = floorY + wallH * 0.5;

  const addSeg = (
    x: number,
    y: number,
    z: number,
    w: number,
    h: number,
    d: number,
    mat: THREE.Material,
  ) => {
    if (w < 0.08 || h < 0.08 || d < 0.08) return;
    const geo = new THREE.BoxGeometry(w, h, d);
    setBoxWorldUVs(geo, w, h, d, 2.0);
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(x, y, z);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    group.add(mesh);
    solids.push(makeAABB(x, y, z, w * 0.5, h * 0.5, d * 0.5));
  };

  const doorOn = (side: DoorSide): RoomDoor | undefined =>
    doors.find((d) => d.side === side);

  // North (+Z)
  {
    const door = doorOn('n');
    const z = maxZ + wallT * 0.5;
    if (!door) {
      addSeg(cx, midY, z, sx + wallT * 2, wallH, wallT, wallMat);
    } else {
      const dw = door.width;
      const dh = door.height ?? doorH;
      const along = minX + door.t * sx;
      const leftW = Math.max(0, along - dw * 0.5 - minX);
      const rightW = Math.max(0, maxX - (along + dw * 0.5));
      const leftC = minX + leftW * 0.5;
      const rightC = maxX - rightW * 0.5;
      addSeg(leftC, midY, z, leftW, wallH, wallT, wallMat);
      addSeg(rightC, midY, z, rightW, wallH, wallT, wallMat);
      // Lintel above door
      addSeg(along, floorY + dh + (wallH - dh) * 0.5, z, dw + 0.15, wallH - dh, wallT, wallMat);
      // Door frame posts
      addSeg(along - dw * 0.5, floorY + dh * 0.5, z, 0.12, dh, wallT + 0.06, mats.wood);
      addSeg(along + dw * 0.5, floorY + dh * 0.5, z, 0.12, dh, wallT + 0.06, mats.wood);
    }
  }

  // South (−Z)
  {
    const door = doorOn('s');
    const z = minZ - wallT * 0.5;
    if (!door) {
      addSeg(cx, midY, z, sx + wallT * 2, wallH, wallT, wallMat);
    } else {
      const dw = door.width;
      const dh = door.height ?? doorH;
      const along = minX + door.t * sx;
      const leftW = Math.max(0, along - dw * 0.5 - minX);
      const rightW = Math.max(0, maxX - (along + dw * 0.5));
      addSeg(minX + leftW * 0.5, midY, z, leftW, wallH, wallT, wallMat);
      addSeg(maxX - rightW * 0.5, midY, z, rightW, wallH, wallT, wallMat);
      addSeg(along, floorY + dh + (wallH - dh) * 0.5, z, dw + 0.15, wallH - dh, wallT, wallMat);
      addSeg(along - dw * 0.5, floorY + dh * 0.5, z, 0.12, dh, wallT + 0.06, mats.wood);
      addSeg(along + dw * 0.5, floorY + dh * 0.5, z, 0.12, dh, wallT + 0.06, mats.wood);
    }
  }

  // East (+X)
  {
    const door = doorOn('e');
    const x = maxX + wallT * 0.5;
    if (!door) {
      addSeg(x, midY, cz, wallT, wallH, sz + wallT * 2, wallMat);
    } else {
      const dw = door.width;
      const dh = door.height ?? doorH;
      const along = minZ + door.t * sz;
      const lowW = Math.max(0, along - dw * 0.5 - minZ);
      const highW = Math.max(0, maxZ - (along + dw * 0.5));
      addSeg(x, midY, minZ + lowW * 0.5, wallT, wallH, lowW, wallMat);
      addSeg(x, midY, maxZ - highW * 0.5, wallT, wallH, highW, wallMat);
      addSeg(x, floorY + dh + (wallH - dh) * 0.5, along, wallT, wallH - dh, dw + 0.15, wallMat);
      addSeg(x, floorY + dh * 0.5, along - dw * 0.5, wallT + 0.06, dh, 0.12, mats.wood);
      addSeg(x, floorY + dh * 0.5, along + dw * 0.5, wallT + 0.06, dh, 0.12, mats.wood);
    }
  }

  // West (−X)
  {
    const door = doorOn('w');
    const x = minX - wallT * 0.5;
    if (!door) {
      addSeg(x, midY, cz, wallT, wallH, sz + wallT * 2, wallMat);
    } else {
      const dw = door.width;
      const dh = door.height ?? doorH;
      const along = minZ + door.t * sz;
      const lowW = Math.max(0, along - dw * 0.5 - minZ);
      const highW = Math.max(0, maxZ - (along + dw * 0.5));
      addSeg(x, midY, minZ + lowW * 0.5, wallT, wallH, lowW, wallMat);
      addSeg(x, midY, maxZ - highW * 0.5, wallT, wallH, highW, wallMat);
      addSeg(x, floorY + dh + (wallH - dh) * 0.5, along, wallT, wallH - dh, dw + 0.15, wallMat);
      addSeg(x, floorY + dh * 0.5, along - dw * 0.5, wallT + 0.06, dh, 0.12, mats.wood);
      addSeg(x, floorY + dh * 0.5, along + dw * 0.5, wallT + 0.06, dh, 0.12, mats.wood);
    }
  }

  if (ceiling) {
    const ceilY = floorY + wallH;
    const ceilGeo = new THREE.BoxGeometry(sx + wallT * 2, 0.35, sz + wallT * 2);
    setBoxWorldUVs(ceilGeo, sx, 0.35, sz, 2.2);
    const ceil = new THREE.Mesh(ceilGeo, ceilMat);
    ceil.position.set(cx, ceilY + 0.15, cz);
    ceil.castShadow = true;
    ceil.receiveShadow = true;
    group.add(ceil);
    solids.push(makeAABB(cx, ceilY + 0.15, cz, sx * 0.5 + wallT, 0.2, sz * 0.5 + wallT));
  }

  if (opts.lit !== false && ceiling) {
    const light = new THREE.PointLight(0xffc9a0, 1.15, Math.max(sx, sz) * 1.4, 1.6);
    light.position.set(cx, floorY + wallH * 0.72, cz);
    group.add(light);
  }

  // Corner moss tufts
  for (const [ox, oz] of [
    [minX + 0.55, minZ + 0.55],
    [maxX - 0.55, minZ + 0.55],
    [minX + 0.55, maxZ - 0.55],
    [maxX - 0.55, maxZ - 0.55],
  ] as const) {
    const clump = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.1, 0.4), mats.moss);
    clump.position.set(ox, floorY + 0.06, oz);
    clump.receiveShadow = true;
    group.add(clump);
  }

  return {
    id: opts.id,
    name: opts.name,
    minX,
    maxX,
    minZ,
    maxZ,
    floorY,
    ceilingY: floorY + wallH,
  };
}

function addCrate(
  parent: THREE.Object3D,
  mats: KeepMaterials,
  solids: AABB[],
  x: number,
  y: number,
  z: number,
  s = 0.55,
): void {
  const geo = new THREE.BoxGeometry(s, s, s);
  setBoxWorldUVs(geo, s, s, s, 0.7);
  const mesh = new THREE.Mesh(geo, mats.wood);
  mesh.position.set(x, y + s * 0.5, z);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  parent.add(mesh);
  solids.push(makeAABB(x, y + s * 0.5, z, s * 0.5, s * 0.5, s * 0.5));
}

function addPillar(
  parent: THREE.Object3D,
  mats: KeepMaterials,
  solids: AABB[],
  x: number,
  z: number,
  h = 3.0,
): void {
  const mesh = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.26, h, 10), mats.wallDark);
  mesh.position.set(x, h * 0.5, z);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  parent.add(mesh);
  solids.push(makeAABB(x, h * 0.5, z, 0.28, h * 0.5, 0.28));
}

function addBench(
  parent: THREE.Object3D,
  mats: KeepMaterials,
  solids: AABB[],
  x: number,
  z: number,
  yaw = 0,
): void {
  const g = new THREE.Group();
  g.position.set(x, 0, z);
  g.rotation.y = yaw;
  const seat = new THREE.Mesh(new THREE.BoxGeometry(1.4, 0.12, 0.4), mats.wood);
  seat.position.y = 0.42;
  const legL = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.4, 0.35), mats.wood);
  legL.position.set(-0.55, 0.2, 0);
  const legR = legL.clone();
  legR.position.x = 0.55;
  g.add(seat, legL, legR);
  g.traverse((o) => {
    if ((o as THREE.Mesh).isMesh) {
      o.castShadow = true;
      o.receiveShadow = true;
    }
  });
  parent.add(g);
  solids.push(makeAABB(x, 0.35, z, 0.75, 0.35, 0.3));
}

/**
 * Courtyard (room 0) sealed with real walls + 10 more fully enclosed chambers.
 * Layout: south spine with side rooms — every exit is a doorway.
 */
export function buildKeepWing(
  parent: THREE.Object3D,
  mats: KeepMaterials,
  solids: AABB[],
  courtyard: { minX: number; maxX: number; minZ: number; maxZ: number },
): RoomInfo[] {
  const rooms: RoomInfo[] = [];
  const wing = new THREE.Group();
  wing.name = 'KeepWing';
  parent.add(wing);

  const wallT = 0.5;
  const wallH = 3.6;
  const doorW = 1.55;

  // —— Room 0: Courtyard (open sky, enclosed walls) ——
  const r0 = buildEnclosedRoom(wing, mats, solids, {
    id: 'r0',
    name: 'Courtyard',
    minX: courtyard.minX,
    maxX: courtyard.maxX,
    minZ: courtyard.minZ,
    maxZ: courtyard.maxZ,
    floorY: 0,
    wallH: 5.2,
    wallT,
    ceiling: false,
    lit: false,
    floorMat: mats.ground,
    wallMat: mats.wallWarm,
    doors: [
      { side: 'n', t: 0.5, width: 3.2, height: 3.4 }, // toward gate
      { side: 's', t: 0.5, width: doorW, height: 2.4 }, // into keep
    ],
  });
  rooms.push(r0);

  const spineW = 8.2;
  const spineD = 7.4;
  const sideW = 6.8;
  const sideD = 6.6;
  const cx = (courtyard.minX + courtyard.maxX) * 0.5;

  // Helper: room immediately south of previous spine room
  let spineMinZ = courtyard.minZ;

  const makeSpine = (
    id: string,
    name: string,
    depth: number,
    doors: RoomDoor[],
    extras?: (info: RoomInfo) => void,
  ): RoomInfo => {
    const maxZ = spineMinZ - wallT;
    const minZ = maxZ - depth;
    const minX = cx - spineW * 0.5;
    const maxX = cx + spineW * 0.5;
    const info = buildEnclosedRoom(wing, mats, solids, {
      id,
      name,
      minX,
      maxX,
      minZ,
      maxZ,
      wallH,
      wallT,
      ceiling: true,
      floorMat: mats.paving,
      wallMat: mats.wall,
      ceilMat: mats.wallDark,
      doors,
    });
    rooms.push(info);
    extras?.(info);
    spineMinZ = minZ;
    return info;
  };

  const makeSide = (
    id: string,
    name: string,
    host: RoomInfo,
    side: 'e' | 'w',
    hostDoorT: number,
  ): RoomInfo => {
    const doorZ = host.minZ + hostDoorT * (host.maxZ - host.minZ);
    const minZ = doorZ - sideD * 0.5;
    const maxZ = doorZ + sideD * 0.5;
    let minX: number;
    let maxX: number;
    if (side === 'e') {
      minX = host.maxX + wallT;
      maxX = minX + sideW;
    } else {
      maxX = host.minX - wallT;
      minX = maxX - sideW;
    }
    const tOnSide = THREE.MathUtils.clamp((doorZ - minZ) / (maxZ - minZ), 0.2, 0.8);
    const doors: RoomDoor[] =
      side === 'e' ? [{ side: 'w', t: tOnSide, width: doorW }] : [{ side: 'e', t: tOnSide, width: doorW }];

    const info = buildEnclosedRoom(wing, mats, solids, {
      id,
      name,
      minX,
      maxX,
      minZ,
      maxZ,
      wallH,
      wallT,
      ceiling: true,
      floorMat: id.includes('cell') ? mats.wallDark : mats.paving,
      wallMat: mats.wallDark,
      doors,
    });
    rooms.push(info);
    return info;
  };

  // R1 Vestibule — connected north to courtyard
  const r1 = makeSpine(
    'r1',
    'Vestibule',
    spineD,
    [
      { side: 'n', t: 0.5, width: doorW, height: 2.4 },
      { side: 's', t: 0.5, width: doorW },
      { side: 'e', t: 0.5, width: doorW },
      { side: 'w', t: 0.5, width: doorW },
    ],
    (info) => {
      addPillar(wing, mats, solids, info.minX + 1.6, info.minZ + 1.6);
      addPillar(wing, mats, solids, info.maxX - 1.6, info.minZ + 1.6);
      addPillar(wing, mats, solids, info.minX + 1.6, info.maxZ - 1.6);
      addPillar(wing, mats, solids, info.maxX - 1.6, info.maxZ - 1.6);
    },
  );

  // R2 / R3 side rooms off vestibule
  const r2 = makeSide('r2', 'Guard Cell', r1, 'w', 0.5);
  addCrate(wing, mats, solids, (r2.minX + r2.maxX) * 0.5, 0, (r2.minZ + r2.maxZ) * 0.5 - 1.2);
  addBench(wing, mats, solids, (r2.minX + r2.maxX) * 0.5, r2.maxZ - 1.2, Math.PI);

  const r3 = makeSide('r3', 'Chapel Nook', r1, 'e', 0.5);
  addBench(wing, mats, solids, (r3.minX + r3.maxX) * 0.5, r3.minZ + 1.3, 0);
  addPillar(wing, mats, solids, (r3.minX + r3.maxX) * 0.5, (r3.minZ + r3.maxZ) * 0.5, 2.6);

  // R4 Hall
  const r4 = makeSpine(
    'r4',
    'Lower Hall',
    spineD,
    [
      { side: 'n', t: 0.5, width: doorW },
      { side: 's', t: 0.5, width: doorW },
      { side: 'e', t: 0.45, width: doorW },
      { side: 'w', t: 0.55, width: doorW },
    ],
    (info) => {
      addBench(wing, mats, solids, info.minX + 1.5, (info.minZ + info.maxZ) * 0.5, Math.PI / 2);
      addBench(wing, mats, solids, info.maxX - 1.5, (info.minZ + info.maxZ) * 0.5, -Math.PI / 2);
    },
  );

  const r5 = makeSide('r5', 'Store Room', r4, 'w', 0.55);
  addCrate(wing, mats, solids, r5.minX + 1.5, 0, r5.minZ + 1.5);
  addCrate(wing, mats, solids, r5.minX + 2.2, 0, r5.minZ + 1.5, 0.45);
  addCrate(wing, mats, solids, r5.maxX - 1.6, 0, r5.maxZ - 1.5, 0.65);

  const r6 = makeSide('r6', 'Armory', r4, 'e', 0.45);
  addCrate(wing, mats, solids, r6.maxX - 1.4, 0, (r6.minZ + r6.maxZ) * 0.5, 0.5);
  addPillar(wing, mats, solids, r6.minX + 1.8, r6.minZ + 1.8, 2.8);

  // R7 Barracks corridor
  const r7 = makeSpine(
    'r7',
    'Barracks',
    spineD,
    [
      { side: 'n', t: 0.5, width: doorW },
      { side: 's', t: 0.5, width: doorW },
      { side: 'e', t: 0.5, width: doorW },
      { side: 'w', t: 0.5, width: doorW },
    ],
    (info) => {
      addBench(wing, mats, solids, info.minX + 1.8, info.minZ + 2.0, Math.PI / 2);
      addBench(wing, mats, solids, info.minX + 1.8, info.maxZ - 2.0, Math.PI / 2);
      addBench(wing, mats, solids, info.maxX - 1.8, info.minZ + 2.0, -Math.PI / 2);
      addBench(wing, mats, solids, info.maxX - 1.8, info.maxZ - 2.0, -Math.PI / 2);
    },
  );

  const r8 = makeSide('r8', 'Mess', r7, 'w', 0.5);
  addCrate(wing, mats, solids, (r8.minX + r8.maxX) * 0.5, 0, (r8.minZ + r8.maxZ) * 0.5);
  addBench(wing, mats, solids, r8.minX + 1.5, (r8.minZ + r8.maxZ) * 0.5, Math.PI / 2);

  const r9 = makeSide('r9', 'Gallery', r7, 'e', 0.5);
  addPillar(wing, mats, solids, r9.minX + 1.7, r9.minZ + 1.7);
  addPillar(wing, mats, solids, r9.maxX - 1.7, r9.minZ + 1.7);
  addPillar(wing, mats, solids, r9.minX + 1.7, r9.maxZ - 1.7);
  addPillar(wing, mats, solids, r9.maxX - 1.7, r9.maxZ - 1.7);

  // R10 Sanctum — dead end
  const r10 = makeSpine(
    'r10',
    'Sanctum',
    spineD + 0.8,
    [{ side: 'n', t: 0.5, width: doorW }],
    (info) => {
      addPillar(wing, mats, solids, (info.minX + info.maxX) * 0.5 - 1.5, info.minZ + 2.2, 3.1);
      addPillar(wing, mats, solids, (info.minX + info.maxX) * 0.5 + 1.5, info.minZ + 2.2, 3.1);
      const dais = new THREE.Mesh(new THREE.BoxGeometry(2.4, 0.35, 1.6), mats.wallWarm);
      setBoxWorldUVs(dais.geometry as THREE.BoxGeometry, 2.4, 0.35, 1.6, 1.2);
      dais.position.set((info.minX + info.maxX) * 0.5, 0.18, info.minZ + 2.8);
      dais.castShadow = true;
      dais.receiveShadow = true;
      wing.add(dais);
      solids.push(
        makeAABB(
          (info.minX + info.maxX) * 0.5,
          0.18,
          info.minZ + 2.8,
          1.2,
          0.2,
          0.8,
        ),
      );
    },
  );

  void r10;
  return rooms;
}

/** Expand play bounds to cover every room plus a small margin. */
export function boundsFromRooms(
  rooms: RoomInfo[],
  killY: number,
): { minX: number; maxX: number; minZ: number; maxZ: number; killY: number } {
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const r of rooms) {
    minX = Math.min(minX, r.minX);
    maxX = Math.max(maxX, r.maxX);
    minZ = Math.min(minZ, r.minZ);
    maxZ = Math.max(maxZ, r.maxZ);
  }
  const pad = 2.0;
  return {
    minX: minX - pad,
    maxX: maxX + pad,
    minZ: minZ - pad,
    maxZ: maxZ + pad,
    killY,
  };
}
